import { castToBoolean, uuidv4 } from '../../../../../lib/util';
import { createError } from "../../../../../lib/error";
import { BLACKOUT_DAYS_TYPES } from '../../../../../lib/service/ProgramService';
import {
  boundFromValidDates,
  formatMinutesDuration,
  PROGRAM_DATE_FORMATS,
  RECAP_TIMESTAMP_FORMATS,
  todayAtClockTime,
} from '../../../../../lib/dateutil';
class AdhocModalController {
  constructor(
    $scope,
    $rootScope,
    $uibModalInstance,
    $q,
    $timeout,
    LogService,
    staffUser,
    GeoService,
    VenueService,
    StaffUserService,
    geolocation,
    translateFilter,
    moment,
    DataSyncService
  ) {
    "ngInject";
    this.$scope            = $scope;
    this.$rootScope        = $rootScope;
    this.$uibModalInstance = $uibModalInstance;
    this.$q                = $q;
    this.$timeout = $timeout;
    this.LogService        = LogService;
    this.GeoService        = GeoService;
    this.VenueService      = VenueService;
    this.StaffUserService  = StaffUserService;
    this.DataSyncService   = DataSyncService;
    this.geolocation       = geolocation;
    this.translateFilter   = translateFilter;
    this.moment            = moment;
    this.minMaxTimeError   = false;
    this.scheduleRuleError = null;
    this.scheduleWarningMessage = null;

    this._errors           = [];
    this._location         = null;
    this._GPSStatusMessage = false;
    this._placesLoading    = false;
    this.saving    = false;
    this.staffUser         = staffUser;
    this.accordions        = {
      locationSearch: true,
      locationSearchSelection: false
    };

    this.lat  = false;
    this.lng = false;

    this._eventDatePickerOptions = {minDate: null, maxDate:null};

    this.adhocForm = {};
    this.adhocForm.start_time = new Date().setHours(9,0,0);
    this.adhocForm.end_time = new Date().setHours(17,0,0);
    // The pickers' ng-change only fires for the user, so this tells a chosen
    // time apart from a default that a program change may still replace.
    this.timesEditedByUser = false;
    this.adhocForm.venueSearch = {};
    this.adhocForm.venueSearch.nearbyOnly = true; // Default state when searching for venues

    // Generate UUID so this event can only be saved once
    this.adhocForm.app_uuid = uuidv4();

    this.errors = [];
  }

  $onInit() {
    // Auto-select the only program when there's exactly one, mirroring a user
    // selection so downstream fields (event type, recap, position, custom
    // fields) cascade. Single-option selects are hidden in the template, which
    // shows the selected value's name instead.
    const programOptions = this.selectProgramOptions();
    if (programOptions.length === 1 && this.adhocForm.program == null) {
      this.adhocForm.program = programOptions[0];
      this.onProgramChanged();
    }

    // Default the event date to today once the date section becomes available
    // (after program/event type/recap are chosen, manually or auto-selected).
    this.$scope.$watch(() => this.canDisplaySection('date'), (canShow) => {
      if (canShow && !this.adhocForm.event_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.adhocForm.event_date = today;
        // Clamps to the program/recap window; nulls it if today is out of range.
        this.verifyEventDateValidity();
      }
    });

    this.GPSStatusMessage = "GETTING_GPS_LOCATION";
    this.geolocation.getLocation().then((data) => {
      this.lat = data.coords.latitude;
      this.lng = data.coords.longitude;
      this.GeoService.latLngLookup(this.lat + ',' + this.lng)
        .then((response) => {
          this.GPSStatusMessage = false;
          this._location = response.data.results[0].formatted_address;
        });
    })
      .catch(() => {
        this.GPSStatusMessage = "GPS_LOCATION_FAILED";
      });
  }

  _addError(error) {
    this.errors.push(error);
  }

  _clearErrors() {
    this.errors.length = 0;
  }

  updateAvailableCustomFields() {
    if (this.adhocForm.program && this.adhocForm.event_type) {
      let fields = [];
      let sortedFields = [];
      if (this.adhocForm.program) {
        fields = fields.concat(this.adhocForm.program.custom_fields);
      }
      if (this.adhocForm.event_type) {
        fields = fields.concat(this.adhocForm.event_type.custom_fields);
      }

      fields.sort((a, b) => {
        if (parseInt(a.display_order) < parseInt(b.display_order)) {
          return -1;
        }
        if (parseInt(a.display_order) > parseInt(b.display_order)) {
          return 1;
        }
        return 0;
      });

      for (const field of fields) {
        if (
          !sortedFields.find((el) => {
            return parseInt(el.id) === parseInt(field.id);
          })
        ) {
          sortedFields.push(field);
        }
      }
      this.adhocForm.custom_fields = sortedFields;
    }
  }

  get location() {
    return this._location;
  }

  set eventDatePickerOpened(opened) {
    this._eventDatePickerOpened = opened;
  }

  get placesLoading() {
    return this._placesLoading;
  }

  set placesLoading( val ) {
    this._placesLoading = val;
  }

  get eventDatePickerOpened() {
    return this._eventDatePickerOpened;
  }

  set eventDatePickerOptions(val) {
    this._eventDatePickerOptions = val;
  }
  get eventDatePickerOptions() {
    return this._eventDatePickerOptions;
  }

  set GPSStatusMessage(message) {

    if (!message) {
      this._GPSStatusMessage = '';
    } else {
      this._GPSStatusMessage = this.translateFilter(message);
    }

  }
  get GPSStatusMessage() {
    return this._GPSStatusMessage;
  }

  close() {
    this.$uibModalInstance.dismiss('close');
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }

  /**
   * Rebuilds the error list from every rule failing right now, and reports
   * whether the form may be saved. Every failing rule is listed together: a date
   * and a duration problem are independent, and fixing one only to be shown the
   * other reads as a new failure. Duration is skipped while a time is unset,
   * since it would then report a meaningless violation on top of the
   * missing-time error.
   *
   * @returns boolean
   */
  collectValidationErrors() {
    this._clearErrors();

    const validTimes = this.verifyEventTimeValidity();
    if (!validTimes) {
      this._addError(createError({
        error: 'EventTimeError',
        message: 'Start Time and End Time hour values must be 1 or greater (12 hour time format).',
        key: 'ERROR_EVENT_START_END_TIMES_NOT_VALID',
      }));
    }

    if (validTimes && !this.verifyProgramMeetsMinMaxTimes()) {
      this._addError(createError({
        error: 'EventDurationError',
        message: this.minMaxTimeError,
        key: 'ERROR_EVENT_DURATION_OUTSIDE_MIN_MAX',
      }));
    }

    if (!this.verifyEventBlackoutDayValidity()) {
      this._addError(createError({
        error: 'EventDateError',
        message: 'Event must not be scheduled on blackout date.',
        key: 'ERROR_EVENT_DATE_ON_BLACKOUT_DATE',
      }));
    }

    if (!this.refreshEventScheduleMessages()) {
      this._addError(createError({
        error: 'EventDateError',
        message: this.scheduleRuleError,
        key: 'ERROR_EVENT_DATE_INSIDE_SCHEDULE_RULE',
      }));
    }

    return !this.errors.length;
  }

  /**
   * Re-runs the whole rule set after an edit, rather than clearing the list: a
   * blackout date has no inline message of its own, so dropping it on an
   * unrelated change would hide a violation that is still true. Silent until a
   * save has been attempted, so a form being filled in is not pre-emptively
   * marked up.
   */
  revalidateAfterChange() {
    if (!this.errors.length) {
      return;
    }

    this.collectValidationErrors();
  }

  /**
   * Every change handler revalidates last. The helpers above it mutate the form
   * — verifyEventDateValidity() nulls a date outside the window, and
   * updateFormDefaultSelections() can auto-select a field the rules read — so a
   * list rebuilt before them describes a form state that no longer exists.
   */
  onProgramChanged() {
    this.applyClientDefaultTimes();
    this.verifyEventDateValidity();
    this.refreshEventScheduleMessages();
    this.refreshTimeMessages();
    this.updateAvailableCustomFields();
    this.updateFormDefaultSelections();
    this.revalidateAfterChange();
  }

  onEventTypeChanged() {
    // The limits can come from the event type, so a type change alone can make
    // the current times valid or invalid.
    this.refreshTimeMessages();
    this.updateAvailableCustomFields();
    this.updateFormDefaultSelections();
    this.revalidateAfterChange();
  }

  onRecapDefinitionChanged() {
    this.verifyEventDateValidity();
    this.refreshEventScheduleMessages();
    this.updateFormDefaultSelections();
    this.revalidateAfterChange();
  }

  onDateChanged() {
    this.verifyEventDateValidity();
    this.refreshEventScheduleMessages();
    // The duration is independent of the date, but its inline message still has
    // to keep up while no save has been attempted.
    this.refreshTimeMessages();
    this.revalidateAfterChange();
  }

  /**
   * Keeps the duration message current without reporting an unset time, which
   * the user has not necessarily reached yet.
   */
  refreshTimeMessages() {
    if (!this.adhocForm.start_time || !this.adhocForm.end_time) {
      this.minMaxTimeError = false;
      return;
    }

    this.verifyProgramMeetsMinMaxTimes();
  }

  /**
   * Event Schedule Rule blocks the save with the configured message; the warning
   * only displays. Both are resolved server-side onto the program.
   *
   * @returns boolean whether the date satisfies the rule
   */
  refreshEventScheduleMessages() {
    const program = this.adhocForm.program;
    if (!program) {
      this.scheduleRuleError = null;
      this.scheduleWarningMessage = null;

      return true;
    }

    this.scheduleRuleError = program.eventScheduleRuleViolation(this.adhocForm.event_date);
    // The rule already blocks this date, so its advisory warning is just noise.
    this.scheduleWarningMessage = this.scheduleRuleError
      ? null
      : program.eventScheduleWarning(this.adhocForm.event_date);

    return !this.scheduleRuleError;
  }

  onTimeChanges() {
    this.timesEditedByUser = true;
    // Validity first: it writes to minMaxTimeError too, so running it second
    // cleared the duration message the moment it was set.
    if (this.verifyEventTimeValidity()) {
      this.verifyProgramMeetsMinMaxTimes();
    }

    this.revalidateAfterChange();
  }

  updateFormDefaultSelections() {
    /*
     * Define select fields on this.adhocForm for which we would like to automatically select
     * when there is exactly one available option (MJOL-2886).
     * - key is form field name
     * - value is function to retrieve available options
     */
    const selectInputsToAutoSelectWhenOneOption = {
      event_type: () => this.selectEventTypeOptions(),
      recap_definition: () => this.selectRecapDefinitionOptions(),
      position: () => this.selectPositionOptions(),
    };

    Object.keys(selectInputsToAutoSelectWhenOneOption).forEach(field => {
      const currVal = this.adhocForm[field];
      // Only apply auto selection logic when value is null
      if (currVal == null) {
        const options = selectInputsToAutoSelectWhenOneOption[field]();
        // Only auto select when only one option is available
        if (options.length === 1) {
          this.$timeout(() => {
            // Use $timeout to perform change in a separate event loop, so that subsequent $digest will trigger an update
            this.adhocForm[field] = options[0];
          });
        }
      }
    });
  }

  selectProgramOptions() {
    return (this.staffUser.programs || []).filter(item => castToBoolean(item.enable_adhoc));
  }

  selectEventTypeOptions() {
    const programEventTypes = this.adhocForm && this.adhocForm.program && this.adhocForm.program.event_types || [];
    return programEventTypes.filter(item => castToBoolean(item.can_be_scheduled) === true);
  }

  selectRecapDefinitionOptions() {
    const programRecapDefinitions = this.adhocForm && this.adhocForm.program && this.adhocForm.program.recap_definitions || [];
    return programRecapDefinitions.filter(item => castToBoolean(item.active) && castToBoolean(item.adhoc));
  }

  selectPositionOptions() {
    return this.adhocForm && this.adhocForm.program && this.adhocForm.program.positions || [];
  }

  placesLookup() {
    // If the event type changes the available google places types may also change and must reload
    if (!this.eventTypeWatcherExists) {
      this.$scope.$watch(() => {
          return this.adhocForm.event_type;
        },
        () => {
          this.doPlacesLookup();
        }
      );
      this.eventTypeWatcherExists = true;
    }

    this.geolocation.getLocation().then((data) => {
      if (data.coords.latitude !== this.lat || data.coords.longitude !== this.lng) {
        this.lat = data.coords.latitude;
        this.lng = data.coords.longitude;
        this.doPlacesLookup();
      }
    });
  }

  doPlacesLookup() {
    // No lat and lng?  Watch for those to become available.
    if (!(this.lat === this.lng) && !this.coordinatesWatcherExists) {
      this.$scope.$watch(
        () => {
          return !!this.lat && !!this.lng;
        },
        () => {
          this.doPlacesLookup();
        }
      );
      this.coordinatesWatcherExists = true;
      return;
    }

    if (this.placesLoading > 0 || !this.lat || !this.lng) {
      return;
    }

    this.placesList = false;
    const googlePlacesEnabled = this.googlePlacesEnabled();
    this.placesLoading = googlePlacesEnabled ? 2 : 1;

    if (googlePlacesEnabled) {
      this.GeoService.placeSearch(this.lat + ',' + this.lng, this.adhocForm.event_type.google_place_types, this.staffUser.profile.units)
        .then((response) => {
          this.placesList = response.data;
          this.placesLoading -= 1;
          // Set the search result coordinates flag with current location. This prevents
          // reloading from Places result when selecting the tab if result exists.
          this._coordinatesOfPlaceSearchResult = this.lat + ',' + this.lng;
        })
        .catch(() => {
          this.LogService.log("No lookup - GPS failed");
        });
    }

    this.VenueService.nearbyVenueSearch(this.lat + ',' + this.lng, this.adhocForm.program.clients_id, 0.5)
      .then((response) => {
        this.nearbyPlacesList = response.data;
        this.placesLoading -= 1;
      }).
      catch(() => {
        this.LogService.log("No lookup - GPS failed");
      });
  }

  /**
   * The program/recap window a date has to fall inside. One builder for the
   * picker bounds and for the clamp below, so a typed date is held to the same
   * window the picker offers.
   *
   * The Event Schedule Rule is only part of the picker's window. It is reported
   * with the client's configured message by refreshEventScheduleMessages() and
   * blocks the save, so clamping on it as well would silently discard the date
   * and take the explanation with it.
   *
   * @param {boolean} includeScheduleRule grey out the days the rule disallows
   *
   * @returns {?{minDate: ?Date, maxDate: ?Date}} null when no window applies
   */
  _eventDateWindow({ includeScheduleRule = false } = {}) {
    if (!this.adhocForm || !this.adhocForm.program || !this.adhocForm.recap_definition) {
      return null;
    }

    const program = this.adhocForm.program;
    const recapDefinition = this.adhocForm.recap_definition;
    const scheduleRuleDate = includeScheduleRule ? program.earliestScheduleRuleDate() : null;
    // An absent date is no bound at all. It has to be kept away from moment()
    // rather than left to isValid(), because moment(undefined) is *now* — that
    // would bound the window at today and clamp away every earlier date.
    const parsed = (value, format) => (value ? this.moment(value, format) : null);

    // the date formats we're getting back from the program and the recap definition are different
    // and Safari (and ios) doesn't like YYYY-mm-dd ISO format, which is nuts, but it is what it is
    return {
      minDate: boundFromValidDates(this.moment.max, [
        parsed(program.start_date, PROGRAM_DATE_FORMATS),
        parsed(recapDefinition.recap_available_events_start_time, RECAP_TIMESTAMP_FORMATS),
        parsed(scheduleRuleDate),
      ]),
      maxDate: boundFromValidDates(this.moment.min, [
        parsed(program.end_date, PROGRAM_DATE_FORMATS),
        parsed(recapDefinition.recap_available_events_end_time, RECAP_TIMESTAMP_FORMATS),
      ]),
    };
  }

  verifyEventDateValidity() {
    if (!this.adhocForm.event_date) {
      return;
    }

    const dateWindow = this._eventDateWindow();
    if (!dateWindow) {
      this.adhocForm.event_date = null;
      return;
    }

    // An end with nothing parseable behind it is unbounded, not violated.
    const eventDate = new Date(this.adhocForm.event_date);
    const beforeWindow = dateWindow.minDate && eventDate < dateWindow.minDate;
    const afterWindow = dateWindow.maxDate && eventDate > dateWindow.maxDate;

    if (beforeWindow || afterWindow) {
      this.adhocForm.event_date = null;
    }
  }

  verifyEventTimeValidity() {
    if (this.adhocForm.start_time == null) {
      this.minMaxTimeError = "Start Time hour must be set to 1 or greater (12 hour time format).";
      return false;
    }

    if (this.adhocForm.end_time == null) {
      this.minMaxTimeError = "End Time hour must be set to 1 or greater (12 hour time format).";
      return false;
    }

    this.minMaxTimeError = null;
    return true;
  }

  verifyEventBlackoutDayValidity() {
    if (!this.adhocForm.event_date) {
      return true; // no date, so no blackout day to land on
    }
    let eventDate = new Date(this.adhocForm.event_date);
    if (this.adhocForm.program.blackout_type === BLACKOUT_DAYS_TYPES.PROGRAM) {
      const daysOfWeek = this.adhocForm.program.blackout_days.map(Number);
      return !daysOfWeek.includes(eventDate.getDay());
    }
    if (this.adhocForm.program.blackout_type === BLACKOUT_DAYS_TYPES.PROGRAM_CHAIN) {
      const chainsId = this.selectedSearchVenue?.parent_chains_id;
      if (chainsId) {
        const daysOfWeek = this.adhocForm.program.blackout_days[chainsId]?.map(Number);
        // No blackout days configured for this chain, so nothing to violate.
        if (!daysOfWeek) return true;
        return !daysOfWeek.includes(eventDate.getDay());
      }
    }

    return true
  }

  // Error present for min/max times
  set minMaxTimeError(error) {
    this._minMaxTimeError = error;
  }

  get minMaxTimeError() {
    return this._minMaxTimeError;
  }

  verifyProgramMeetsMinMaxTimes() {
    if (!this.adhocForm.program) {
      return true; // no program, so no limits to violate
    }

    if (!this.adhocForm.start_time || !this.adhocForm.end_time) {
      return true; // verifyEventTimeValidity() reports the unset time
    }

    const limits = this.adhocForm.program.eventDurationLimits(this.adhocForm.event_type);

    const start = new Date(this.adhocForm.start_time);
    const end = new Date(this.adhocForm.end_time);
    // If the start time is after the end time, add 24 hours
    if (start > end) {
      end.setDate(end.getDate() + 1);
    }
    const diffMinutes = (end - start) / 1000 / 60;

    // Equal times read as 0 rather than 24 hours, and a zero-length shift is
    // never valid, even where no level sets a minimum.
    if (diffMinutes === 0) {
      this.minMaxTimeError = 'End time must be after start time';
      return false;
    }

    if (limits.min === 0 && limits.max === 0) {
      this.minMaxTimeError = false;
      return true;
    }

    if (limits.min !== 0 && diffMinutes < limits.min) {
      this.minMaxTimeError = `Shift must be at least ${formatMinutesDuration(limits.min)}`;
      return false;
    }
    if (limits.max !== 0 && diffMinutes > limits.max) {
      this.minMaxTimeError = `Shift cannot exceed ${formatMinutesDuration(limits.max)}`;
      return false;
    }
    this.minMaxTimeError = false;
    return true;
  }

  dateDisabled() {
    if (this.adhocForm.program.blackout_type === BLACKOUT_DAYS_TYPES.PROGRAM) {
      const daysOfWeek = this.adhocForm.program.blackout_days.map(Number);
      return (data) => data.mode === 'day' && daysOfWeek.includes(data.date.getDay());
    }

    if (this.adhocForm.program.blackout_type === BLACKOUT_DAYS_TYPES.PROGRAM_CHAIN) {
      const chainsId = this.selectedSearchVenue?.parent_chains_id;
      if (chainsId) {
        const daysOfWeek = this.adhocForm.program.blackout_days[chainsId]?.map(Number);
        if (!daysOfWeek) return () => false;
        return (data) => data.mode === 'day' && daysOfWeek.includes(data.date.getDay());
      }
    }

    return () => false
  }

  openEventDatePicker() {
    const dateWindow = this._eventDateWindow({ includeScheduleRule: true });
    if (!dateWindow) {
      this.adhocForm.event_date = null;
      return; // No program or event type chosen, no date can be chosen
    }

    this._eventDatePickerOptions = {
      minDate: dateWindow.minDate,
      maxDate: dateWindow.maxDate,
      showWeeks: false,
      ...( (this.adhocForm.program.blackout_type === BLACKOUT_DAYS_TYPES.PROGRAM || this.adhocForm.program.blackout_type === BLACKOUT_DAYS_TYPES.PROGRAM_CHAIN) && {dateDisabled: this.dateDisabled() }),
    };

    this.eventDatePickerOpened = true;
  }

  // Search for venues from the search input fields
  venueSearch() {
    const params = {
      street_address1: this.adhocForm.venueSearch.street_address1,
      venue_name: this.adhocForm.venueSearch.venue_name,
      state: this.adhocForm.venueSearch.state,
      city: this.adhocForm.venueSearch.city,
      zip: this.adhocForm.venueSearch.zip,
      lat: this.lat,
      lng: this.lng,
      nearby: this.adhocForm.venueSearch.nearbyOnly,
      clients_id: this.adhocForm.program.clients_id
    };

    this.VenueService.venueSearch(params)
      .then((response) => {
        this.venueSearchResults = response.data.venues;
        this.accordions.locationSearch = false;
      })
      .catch(() => {});
  }

  // Selecting a google place result triggers a lookup on the place id.
  selectGooglePlaceResult(value) {
    // If the user clicked on a place result that was already in the nearby venues list
    // pulled from the database that's what they should be seeing
    const venueFromNearbyList = this.nearbyPlacesList.venues.find((el) => {
      return parseInt(el.place_id) === parseInt(value.place_id);
    });
    if (!!venueFromNearbyList) {
      this.selectedSearchVenue = venueFromNearbyList;
      return;
    }

    // Google Place results don't have full info, the API has to be queried for details
    this.GeoService.placeDetail(value.place_id)
      .then((response) => {
        let place = response.data.result;

        // Response fields are not consistently organized, so we have to search for the components
        let street_number = place.address_components.find((val) => {
          return (val.types.indexOf('street_number') > -1);
        });
        let street = place.address_components.find((val) => {
          return (val.types.indexOf('route') > -1);
        });
        let city = place.address_components.find((val) => {
          return (val.types.indexOf('locality') > -1);
        });
        let state = place.address_components.find((val) => {
          return (val.types.indexOf('administrative_area_level_1') > -1);
        });
        let zip = place.address_components.find((val) => {
          return (val.types.indexOf('postal_code') > -1);
        });

        let street_address1 = '';
        if (street_number) {
          street_address1 += street_number.short_name + " ";
        }
        if (street) {
          street_address1 += street.short_name;
        }

        this.selectedSearchVenue = {
          venue_name: place.name,
          street_address1: street_address1,
          city: city.long_name,
          state: state.short_name,
          zip: zip.short_name,
          place_id: value.place_id
        };
      });
  }

  selectSearchVenue(value) {
    this.selectedSearchVenue = value;
  }
  selectYourLocationResult(value) {
    this.selectedSearchVenue = value;
  }

  cancelVenueSelection() {
    this.selectedSearchVenue = false;
  }

  getVenueTypesForProgram() {
    if (!this.adhocForm.program) return [];

    let clientId = this.adhocForm.program.clients_id;
    let client = this.staffUser.clients.find((el) => parseInt(el.id) === parseInt(clientId));
    return client.venue_types;
  }

  getMarketsForProgram() {
    if (!this.adhocForm.program) {
      return [];
    }

    const clientId = this.adhocForm.program.clients_id;
    const client = this.staffUser.clients.find((el) => parseInt(el.id) === parseInt(clientId));
    return client.markets;
  }

  // Resolve the client for the currently selected program (ad-hoc events are
  // always tied to one program/client). Mirrors getVenueTypesForProgram().
  _selectedClient() {
    if (!this.adhocForm.program) {
      return null;
    }
    const clientId = this.adhocForm.program.clients_id;
    return this.staffUser.clients.find((el) => parseInt(el.id) === parseInt(clientId)) || null;
  }

  // Prefill start/end from the selected client's default event times (HH:mm
  // from the sync), keeping 9:00/17:00 for any that is absent or malformed. The
  // client is only known once a program is chosen, and times the user already
  // picked are never overwritten.
  applyClientDefaultTimes() {
    if (this.timesEditedByUser) {
      return;
    }
    const client = this._selectedClient();
    const toTimestamp = (value, fallbackHour) => {
      const timestamp = todayAtClockTime(value);
      return timestamp === null ? new Date().setHours(fallbackHour, 0, 0, 0) : timestamp;
    };
    this.adhocForm.start_time = toTimestamp(client && client.events_default_start, 9);
    this.adhocForm.end_time = toTimestamp(client && client.events_default_end, 17);
  }

  // Whether the selected program's client allows Google places in the nearby search.
  googlePlacesEnabled() {
    const client = this._selectedClient();
    return !!(client && client.staff_can_search_nearby_google_places);
  }

  // When the selected program's client has ad-hoc time hiding enabled, the
  // start/end time inputs are hidden and the event uses the current time plus
  // the configured duration (in minutes).
  hideEventTimes() {
    const client = this._selectedClient();
    return !!(client && client.staff_adhoc_hide_times);
  }

  // When the selected program's client has ad-hoc description hiding enabled,
  // the Description field is hidden on the ad-hoc form.
  hideEventDescription() {
    const client = this._selectedClient();
    return !!(client && client.staff_adhoc_hide_description);
  }

  // Whether the ad-hoc form should collect an event name, driven by the
  // selected program's client "Use Event Name" setting. Shows until a program
  // (and thus client) is chosen; the backend falls back to the program name
  // and event id when no name is provided.
  useEventName() {
    const client = this._selectedClient();
    if (!client) {
      return true;
    }
    return !!client.events_has_name;
  }

  canDisplaySection(section) {
    switch(section) {
      case 'date':
        return (
          this.adhocForm.program
          && this.adhocForm.event_type
          && this.adhocForm.recap_definition
        );
      case 'save details':
        return (
          this.canDisplaySection('date')
          && this.adhocForm.event_date
          && this.adhocForm.position
          && this._requiredCustomFieldsAreValid()
        );
      case 'save':
        return (
          this.canDisplaySection('save details')
          && this.selectedSearchVenue
          && this.selectedSearchVenue.markets_id
          && this.selectedSearchVenue.venues_types_id
        );
    }
  }

  _requiredCustomFieldsAreValid() {
    let valid = true;
    if (this.adhocForm.custom_fields) {
      for (const item of this.adhocForm.custom_fields) {
        item.errors = [];
        if (castToBoolean(item.required) && (item.value === null || item.value === undefined)) {
          valid = false;
        }
        if ((item.min_length || item.max_length) && !item.value) {
          valid = false;
        }
      }
    }

    return valid;
  }

  // Takes the address entered in the 'new address' tab and sets it as the selected venue
  addAddress() {
    this.selectedSearchVenue = this.adhocForm.venue;
    let venueTypes = this.getVenueTypesForProgram();
    let venueType = venueTypes.find((val) => {
      return parseInt(val.id) === parseInt(this.adhocForm.venue.venues_types_id);
    });
    this.selectedSearchVenue.venues_types_name = venueType.name;

    let markets = this.getMarketsForProgram();
    let market = markets.find((val) => {
      return parseInt(val.id) === parseInt(this.adhocForm.venue.markets_id);
    });
    this.selectedSearchVenue.markets_name = market.name;
  }

  // Save the adhoc event - we're all done!
  save() {
    if (this.saving) {
      return
    }
    this.saving = true
    this.adhocForm.venue = this.selectedSearchVenue;

    // When ad-hoc time hiding is enabled for this client, the time inputs are
    // hidden; set the start to the current time and the end to that plus the
    // configured duration (in minutes) before any time validation runs.
    if (this.hideEventTimes()) {
      const client = this._selectedClient();
      const durationMinutes = parseInt(client && client.staff_adhoc_hidden_duration, 10) || 0;
      const start = this.moment();
      this.adhocForm.start_time = start.toDate();
      this.adhocForm.end_time = start.clone().add(durationMinutes, 'minutes').toDate();
    }

    // We don't want time in UTC, it's local to the event
    this.adhocForm.start_time_formatted = this.moment(this.adhocForm.start_time).format('h:mm a');
    this.adhocForm.end_time_formatted = this.moment(this.adhocForm.end_time).format('h:mm a');

    /**
     * MJOL-1656 - Reformatting the event date here breaks the form and causes the date value
     * to be invalid / removed. The event seems to be created ok without it.
     */
    //this.adhocForm.event_date = this.moment(this.adhocForm.event_date).format('L');

    if (!this.collectValidationErrors()) {
      this.saving = false
      return;
    }

    return this.StaffUserService.saveAdhoc(this.adhocForm)
      .catch(response => {
        this.saving = false
        this.LogService.error("Error saving ad hoc:", response);

        const errorName = response && response.data && response.data.error || "UknownErrorCreatingAdHoc";
        const errorMessage = response && response.data && response.data.message || "Unknown error";
        let errorKey;
        if (errorName === 'AttemptedToCreateAdHocEventConflictingWithExistingPosition') {
          errorKey = 'ERROR_ATTEMPTED_TO_CREATE_AD_HOC_EVENT_CONFLICTING_WITH_EXISTING_POSITION';
        }
        const error = createError({
          error: errorName,
          message: errorMessage,
          key: errorKey,
          response,
        });
        this._addError(error);
        return this.$q.reject(error);
      })
      .then(response => {
        const createdEvent = response && response.data && response.data.event;

        this.close();
        this.cancel();
        this.saving = false

        // When the staff user has opted in (profile preference), continue
        // straight to the new event's recap. Mirror the guerrilla flow:
        // refresh positions, then open the recap for the matching position.
        // Any failure falls back to a normal dashboard sync so the user is
        // never left without their newly created event.
        const continueToRecap = createdEvent
          && this.staffUser && this.staffUser.profile
          && castToBoolean(this.staffUser.profile.continue_to_recap_after_adhoc);

        // These emits run after this.close() has dismissed the modal and torn
        // down this.$scope, so they must go through $rootScope (both listeners
        // - 'showRecapResponseForPosition' and 'sync' - live on $rootScope).
        if (continueToRecap) {
          return this.DataSyncService.refreshPositions()
            .then(() => {
              const position = this._findPositionForEvent(createdEvent.id);
              if (position) {
                this.$rootScope.$emit('showRecapResponseForPosition', position);
              } else {
                this.LogService.error(
                  'Adhoc continue-to-recap: no position found for event',
                  createdEvent.id
                );
                this.$rootScope.$emit('sync');
              }
            })
            .catch(err => {
              this.LogService.error('Adhoc continue-to-recap refresh failed:', err);
              this.$rootScope.$emit('sync');
            });
        }

        this.$scope.$emit('sync');
      });
  }

  /**
   * Locate the freshly-synced position for a created ad-hoc event so its
   * recap can be opened. Positions carry the event id on `events_id`.
   *
   * @param {number} eventId
   * @returns {object|null}
   */
  _findPositionForEvent(eventId) {
    const positions = (this.staffUser && this.staffUser.positions) || [];
    return positions.find(p => p && Number(p.events_id) === Number(eventId)) || null;
  }
}

export default AdhocModalController;

